// GitHub-style unified line diff used by the skill override history card to
// compare a skill's versions. Kept dependency-free (an LCS over lines) since
// the texts are prompt-sized and we only need line-level granularity.

export type LineDiffOp = 'context' | 'add' | 'remove';

export type LineDiffRow = {
    readonly op: LineDiffOp;
    readonly text: string;
};

export type LineDiffPreview =
    | { readonly kind: 'diff'; readonly rows: Array<LineDiffRow> }
    | {
          readonly kind: 'raw';
          readonly message: 'body too large to diff inline — view raw side-by-side';
      };

const MAX_INLINE_DIFF_CHARS = 32_768;
const MAX_INLINE_DIFF_LINES = 1_000;
const MAX_INLINE_DIFF_CELLS = 250_000;

// Prompt bodies are untrusted. These bounds keep both the LCS allocation and
// the rendered review surface predictable; larger bodies use raw text panels.

function splitLines(value: string): Array<string> {
    return value.length === 0 ? [] : value.split('\n');
}

function canDiffInline(
    before: string,
    after: string,
    beforeLines: Array<string>,
    afterLines: Array<string>,
): boolean {
    return (
        before.length + after.length <= MAX_INLINE_DIFF_CHARS &&
        beforeLines.length + afterLines.length <= MAX_INLINE_DIFF_LINES &&
        beforeLines.length * afterLines.length <= MAX_INLINE_DIFF_CELLS
    );
}

function computeLineDiffUnbounded(
    beforeLines: Array<string>,
    afterLines: Array<string>,
): Array<LineDiffRow> {
    const n = beforeLines.length;
    const m = afterLines.length;

    // lengths[i][j] = LCS length of beforeLines[i:] and afterLines[j:]. This
    // helper is called only after canDiffInline bounds the matrix size.
    const lengths: Array<Array<number>> = Array.from({ length: n + 1 }, () =>
        Array.from({ length: m + 1 }, () => 0),
    );
    for (let i = n - 1; i >= 0; i--) {
        const lengthsAtI = lengths[i]!;
        const lengthsAtNext = lengths[i + 1]!;
        const beforeLine = beforeLines[i]!;
        for (let j = m - 1; j >= 0; j--) {
            lengthsAtI[j] =
                beforeLine === afterLines[j]!
                    ? lengthsAtNext[j + 1]! + 1
                    : Math.max(lengthsAtNext[j]!, lengthsAtI[j + 1]!);
        }
    }

    const rows: Array<LineDiffRow> = [];
    let i = 0;
    let j = 0;
    while (i < n && j < m) {
        if (beforeLines[i]! === afterLines[j]!) {
            rows.push({ op: 'context', text: beforeLines[i]! });
            i++;
            j++;
        } else if (lengths[i + 1]![j]! >= lengths[i]![j + 1]!) {
            rows.push({ op: 'remove', text: beforeLines[i]! });
            i++;
        } else {
            rows.push({ op: 'add', text: afterLines[j]! });
            j++;
        }
    }
    while (i < n) {
        rows.push({ op: 'remove', text: beforeLines[i]! });
        i++;
    }
    while (j < m) {
        rows.push({ op: 'add', text: afterLines[j]! });
        j++;
    }
    return rows;
}

export function computeLineDiffPreview(
    before: string,
    after: string,
): LineDiffPreview {
    const beforeLines = splitLines(before);
    const afterLines = splitLines(after);
    if (!canDiffInline(before, after, beforeLines, afterLines)) {
        return {
            kind: 'raw',
            message: 'body too large to diff inline — view raw side-by-side',
        };
    }
    return {
        kind: 'diff',
        rows: computeLineDiffUnbounded(beforeLines, afterLines),
    };
}

// Unified line diff: unchanged lines are 'context', lines only in `before` are
// 'remove', lines only in `after` are 'add'. Order follows the longest common
// subsequence so unchanged lines stay aligned between the two versions.
export function computeLineDiff(
    before: string,
    after: string,
): Array<LineDiffRow> {
    const preview = computeLineDiffPreview(before, after);
    return preview.kind === 'diff' ? preview.rows : [];
}
